import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBuilding,
  faCheck,
  faClockRotateLeft,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { platformSubscriptionService } from '@/services/platform-subscription.service';
import type { SubscriptionDetail, SubscriptionStatus } from '@/types/platform-subscription.types';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { canPlatform } from '@/lib/platform-permissions';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import {
  SubscriptionLifecycleDialog,
  type SubscriptionLifecycleAction,
} from './SubscriptionLifecycleDialog';
import {
  SubscriptionStatusBadge,
  formatExpiry,
  formatPeriod,
  formatSubscriptionDate,
  formatSubscriptionDateTime,
} from './subscription-presentation';

/**
 * Phase 12.4E — Subscription detail. Authorized by **D-109 §1**.
 *
 * Design of record: `SUBSCRIPTIONS_DESIGN.md` v1.4 §5.2 (five cards), §8.2–§8.6.
 *
 * ── Everything here is read, except four named transitions ──────────────────
 * There is no "Edit Subscription" control, no plan select, no period field and
 * no expiry field, because none of those is an authorized operation (D-5, D-12,
 * D-14, OD-A/OD-B). The only write is Activate / Suspend / Reactivate / Cancel,
 * and each is a named endpoint the backend guards independently.
 */

/** Which transitions are legal from a given status — mirrors §6.3 exactly. */
const ACTIONS_BY_STATUS: Record<SubscriptionStatus, SubscriptionLifecycleAction[]> = {
  TRIAL: ['activate', 'cancel'],
  ACTIVE: ['suspend', 'cancel'],
  SUSPENDED: ['reactivate', 'cancel'],
  // Both terminal: CANCELLED has no transition out (D-9), and nothing produces
  // EXPIRED at all (D-10), so neither offers an action.
  CANCELLED: [],
  EXPIRED: [],
};

const ACTION_LABEL: Record<SubscriptionLifecycleAction, string> = {
  activate: 'Activate',
  suspend: 'Suspend',
  reactivate: 'Reactivate',
  cancel: 'Cancel',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isRoutableId = (value: string | undefined): value is string => !!value && UUID_RE.test(value);

/** Pull the status and server message out of a rejected request. */
function describeError(err: unknown): { status: number | null; message: string } {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status ?? null;
    const body = err.response?.data as { message?: string | string[] } | undefined;
    const raw = body?.message;
    const message = Array.isArray(raw) ? raw.join(' ') : raw;
    if (message) return { status, message };
  }
  if (err instanceof Error && err.message) return { status: null, message: err.message };
  return { status: null, message: 'The action could not be completed. Nothing was changed.' };
}

/** One label/value pair. `value` already carries "—" where a field is unset. */
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-foreground">{value}</dd>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export function SubscriptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = usePlatformAuthStore((s) => s.user?.role);
  const canManage = canPlatform(role, 'platform:subscriptions:manage');

  const [subscription, setSubscription] = useState<SubscriptionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [action, setAction] = useState<SubscriptionLifecycleAction | null>(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');

  const routable = isRoutableId(id);

  const load = useCallback(() => {
    if (!routable) return;
    setLoading(true);
    platformSubscriptionService
      .getById(id)
      .then((data) => {
        setSubscription(data);
        setError('');
      })
      .catch(() => setError('Subscription not found or access denied.'))
      .finally(() => setLoading(false));
  }, [id, routable]);

  useEffect(() => {
    if (!routable) {
      // `replace` so a malformed URL does not linger in history.
      navigate(PLATFORM_ROUTES.SUBSCRIPTIONS, { replace: true });
      return;
    }
    load();
  }, [routable, navigate, load]);

  /**
   * Run a transition.
   *
   * ── On success ────────────────────────────────────────────────────────────
   * Re-read from the server. **Never** patch local state: status, reason, actor
   * and timestamp are all written server-side, and guessing them here is how a
   * console starts showing something the database does not agree with.
   *
   * ── On 409 ────────────────────────────────────────────────────────────────
   * The dialog stays open with the message and the typed reason intact, **and**
   * the record is re-read. A 409 means this view was stale — the status on
   * screen is no longer the status in the database — so refreshing it is part of
   * reporting the conflict honestly, not a separate courtesy.
   */
  const runAction = async (reason: string) => {
    if (!subscription || !action) return;
    setActionSubmitting(true);
    setActionError('');
    try {
      const svc = platformSubscriptionService;
      const subId = subscription.subscriptionId;
      if (action === 'activate') await svc.activate(subId, reason || undefined);
      if (action === 'suspend') await svc.suspend(subId, reason);
      if (action === 'reactivate') await svc.reactivate(subId, reason || undefined);
      if (action === 'cancel') await svc.cancel(subId, reason);

      setAction(null);
      setNotice(`Subscription ${ACTION_LABEL[action].toLowerCase()} completed.`);
      load();
    } catch (err) {
      const { status, message } = describeError(err);
      setActionError(message);
      if (status === 409) load();
    } finally {
      setActionSubmitting(false);
    }
  };

  const backToList = () => navigate(PLATFORM_ROUTES.SUBSCRIPTIONS);

  // The skeleton is for the FIRST load only.
  //
  // A re-read after a lifecycle action is a background refresh, and blanking the
  // page for it would unmount the dialog — which, on a 409, would throw away the
  // reason the operator had just typed. §8.6 requires that reason to survive, so
  // once there is something to show, it keeps being shown while the refresh runs.
  if (loading && !subscription) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="h-6 w-56 animate-pulse rounded bg-muted" />
        <div className="h-40 animate-pulse rounded-xl bg-muted" />
        <div className="h-40 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="space-y-4">
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3"
        >
          <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4 text-destructive" />
          <p className="min-w-0 flex-1 text-sm text-destructive">
            {error || 'Subscription not found.'}
          </p>
        </div>
        <button
          type="button"
          onClick={backToList}
          className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted sm:min-h-0"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
          Back to subscriptions
        </button>
      </div>
    );
  }

  const availableActions = ACTIONS_BY_STATUS[subscription.status];
  const isTerminal = availableActions.length === 0;

  return (
    <div className="space-y-5">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={backToList}
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
          Subscriptions
        </button>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="break-words text-xl font-semibold text-foreground">
              {subscription.company.name}
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">{subscription.company.slug}</p>
          </div>
          <SubscriptionStatusBadge status={subscription.status} size="md" />
        </div>
      </div>

      {/* ── Success notice ─────────────────────────────────────────────────── */}
      {notice && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-xl border border-green-600/30 bg-green-500/5 px-4 py-3"
        >
          <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-green-600" />
          <p className="min-w-0 flex-1 text-sm text-green-700 dark:text-green-400">{notice}</p>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── Overview ─────────────────────────────────────────────────────── */}
        <Card title="Overview">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Status"
              value={<SubscriptionStatusBadge status={subscription.status} />}
            />
            {/* Period and Expires render "—" when unset and are NEVER computed:
                GP-4 is resolved as Option A, and null means "no term recorded". */}
            <Field label="Period" value={formatPeriod(subscription.period)} />
            <Field label="Started" value={formatSubscriptionDate(subscription.startedAt)} />
            <Field label="Expires" value={formatExpiry(subscription.expiresAt)} />
            <Field
              label="Subscription ID"
              value={<span className="font-mono text-xs">{subscription.subscriptionId}</span>}
            />
          </dl>
        </Card>

        {/* ── Company ──────────────────────────────────────────────────────── */}
        <Card title="Company">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Name" value={subscription.company.name} />
            <Field label="Slug" value={subscription.company.slug} />
          </dl>
          <button
            type="button"
            onClick={() =>
              navigate(PLATFORM_ROUTES.COMPANY_DETAIL.replace(':id', subscription.company.tenantId))
            }
            className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted sm:min-h-0"
          >
            <FontAwesomeIcon icon={faBuilding} className="h-3.5 w-3.5" />
            View company
          </button>
        </Card>

        {/* ── Plan — identity only ─────────────────────────────────────────── */}
        <Card title="Plan">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Plan" value={subscription.plan.slug} />
          </dl>
          {/* No price, capabilities, limits, modules or tier framing — D-019,
              GC-1, D-13. The slug is the whole of the approved representation. */}
          <p className="mt-3 text-xs text-muted-foreground">
            Plans are internal assignment records. Pricing and entitlements are not managed here.
          </p>
        </Card>

        {/* ── Lifecycle ────────────────────────────────────────────────────── */}
        <Card title="Lifecycle">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Last reason" value={subscription.statusReason ?? '—'} />
            <Field
              label="Changed at"
              value={formatSubscriptionDateTime(subscription.statusChangedAt)}
            />
            <Field
              label="Changed by"
              value={
                subscription.statusChangedBy ? (
                  <span className="font-mono text-xs">{subscription.statusChangedBy}</span>
                ) : (
                  '—'
                )
              }
            />
            <Field label="Created" value={formatSubscriptionDateTime(subscription.createdAt)} />
          </dl>

          {/* ── Actions ────────────────────────────────────────────────────── */}
          <div className="mt-4 border-t border-border pt-4">
            {canManage ? (
              isTerminal ? (
                <p className="text-sm text-muted-foreground">
                  {subscription.status === 'CANCELLED'
                    ? 'This subscription is cancelled. Cancellation is permanent and cannot be reversed — the record is retained.'
                    : 'This subscription is expired. No further action is available.'}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {availableActions.map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => {
                        setActionError('');
                        setAction(a);
                      }}
                      className={
                        a === 'cancel' || a === 'suspend'
                          ? 'inline-flex min-h-11 items-center gap-2 rounded-lg border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 sm:min-h-0'
                          : 'inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted sm:min-h-0'
                      }
                    >
                      {ACTION_LABEL[a]}
                    </button>
                  ))}
                </div>
              )
            ) : (
              // Hidden, not disabled — a control a role can never use is noise.
              <p className="text-sm text-muted-foreground">
                Your role can view subscriptions but not change their status.
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* ── History ────────────────────────────────────────────────────────── */}
      <Card title="History">
        {subscription.history.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
            <FontAwesomeIcon
              icon={faClockRotateLeft}
              className="mb-2 h-5 w-5 text-muted-foreground/40"
            />
            <p className="text-sm text-muted-foreground">
              No prior subscription records for this company.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              This is the company’s only subscription.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border text-sm">
              <caption className="sr-only">
                Prior subscriptions for {subscription.company.name}, newest first. Read-only.
              </caption>
              <thead className="bg-muted/30">
                <tr>
                  {['Plan', 'Status', 'Period', 'Started', 'Expires'].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {subscription.history.map((h) => (
                  <tr key={h.subscriptionId}>
                    <td className="px-4 py-3 text-muted-foreground">{h.plan.slug}</td>
                    <td className="px-4 py-3">
                      <SubscriptionStatusBadge status={h.status} />
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatPeriod(h.period)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {formatSubscriptionDate(h.startedAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {formatExpiry(h.expiresAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <SubscriptionLifecycleDialog
        action={action}
        companyName={subscription.company.name}
        submitting={actionSubmitting}
        error={actionError}
        onClose={() => setAction(null)}
        onConfirm={runAction}
      />
    </div>
  );
}
